import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * MobileBottomSheet — reusable luxury bottom sheet.
 * Snaps to ~92vh with a gold hairline top border, gold drag handle,
 * gradient darkening backdrop and swipe-down-to-close.
 */
export default function MobileBottomSheet({ open, onClose, title, subtitle, children, testId = "mobile-sheet" }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="sheet-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={onClose}
            className="fixed inset-0 z-[80] bg-[#071A2F]/70 backdrop-blur-md"
            data-testid={`${testId}-backdrop`}
          />
          <motion.div
            key="sheet"
            data-testid={testId}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 320 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(e, info) => {
              if (info.offset.y > 140 || info.velocity.y > 600) onClose();
            }}
            className="fixed left-0 right-0 bottom-0 z-[90] max-h-[92vh] rounded-t-[28px] overflow-hidden flex flex-col"
            style={{
              background: "linear-gradient(180deg, #0b0b0d 0%, #000 100%)",
              borderTop: "1px solid rgba(57,214,255,0.4)",
              boxShadow: "0 -20px 80px -20px rgba(57,214,255,0.35)",
            }}
          >
            {/* Drag handle */}
            <div className="flex flex-col items-center pt-2.5 pb-1">
              <div className="w-10 h-1 rounded-full bg-[#39D6FF]/60" />
            </div>
            {/* Header */}
            <div className="flex items-start justify-between px-6 pt-3 pb-4">
              <div>
                {subtitle && (
                  <div className="text-[9px] tracking-[0.4em] text-[#39D6FF]/70 mb-1">{subtitle}</div>
                )}
                <h2 className="font-serif text-2xl text-white leading-tight">{title}</h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid={`${testId}-close`}
                aria-label="Close"
                className="w-9 h-9 rounded-full flex items-center justify-center bg-white/5 border border-white/10 text-white/70 hover:text-white active:scale-95 transition"
              >
                <X size={16} />
              </button>
            </div>
            <div
              className="flex-1 overflow-y-auto px-6 pb-10 -mt-1"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/** Compact field wrappers used inside the sheet. */
export function SheetField({ label, hint, children, testId }) {
  return (
    <label className="block mb-4" data-testid={testId}>
      <span className="block text-[10px] tracking-[0.35em] text-white/50 mb-1.5 uppercase">{label}</span>
      {children}
      {hint && <span className="block text-[10px] text-white/40 mt-1">{hint}</span>}
    </label>
  );
}

export const sheetInputCls =
  "w-full bg-white/5 border border-white/10 focus:border-[#39D6FF]/60 focus:bg-white/8 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition";

export function GoldButton({ children, disabled, loading, testId, ...rest }) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      data-testid={testId}
      className={`relative w-full py-3.5 rounded-full text-sm tracking-[0.28em] font-bold text-black transition-all active:brightness-90 ${(disabled || loading) ? "opacity-60" : ""}`}
      style={{
        background: "linear-gradient(180deg, #F8FAFC 0%, #39D6FF 50%, #39D6FF 100%)",
        boxShadow: "0 6px 24px -6px rgba(57,214,255,0.6), inset 0 1px 0 rgba(255,255,255,0.55)",
      }}
    >
      {loading ? "Please wait…" : children}
    </button>
  );
}
